import Checkbox from "antd/lib/checkbox";
import Table from "antd/lib/table";
import { SorterResult, TablePaginationConfig } from "antd/lib/table/interface";
import Typography from "antd/lib/typography";
import moment from "moment";
import React from "react";
import config from "~config";
import User from "~types/User";
import { UserSortBy, UserTableProps } from "./UserTable.types";

function UserTable({
    users,
    loading,
    page,
    pageSize,
    total,
    sortBy,
    sortOrder,
    onTableChange,
}: UserTableProps): JSX.Element {
    const resolveSortOrder = (columnSortKey: UserSortBy) =>
        sortBy === columnSortKey ? sortOrder : undefined;

    return (
        <Table
            columns={[
                {
                    key: "emailAddress",
                    dataIndex: "emailAddress",
                    title: "Email address",
                    width: 250,
                    sorter: true,
                    sortOrder: resolveSortOrder("emailAddress"),
                    render: (value) => (
                        <Typography.Link
                            href={`mailto:${value}`}
                            target="_blank"
                        >
                            {value}
                        </Typography.Link>
                    ),
                },
                {
                    key: "administrator",
                    title: "Administrator?",
                    width: 170,
                    sorter: true,
                    sortOrder: resolveSortOrder("administrator"),
                    render: (_, record) => (
                        <Checkbox
                            checked={record.administrator}
                            style={{
                                pointerEvents: "none",
                            }}
                        />
                    ),
                },
                {
                    key: "subscribed",
                    title: "Subscribed?",
                    width: 150,
                    sorter: true,
                    sortOrder: resolveSortOrder("subscribed"),
                    render: (_, record) => (
                        <Checkbox
                            checked={record.remoteLockAccessPerson !== null}
                            style={{
                                pointerEvents: "none",
                            }}
                        />
                    ),
                },
                {
                    key: "name",
                    title: "Name",
                    width: 200,
                    sorter: true,
                    sortOrder: resolveSortOrder("name"),
                    render: (_, record) =>
                        `${record.firstName} ${record.lastName}`,
                },
                {
                    key: "dateOfBirth",
                    dataIndex: "dateOfBirth",
                    title: "Date of birth",
                    width: 150,
                    sorter: true,
                    sortOrder: resolveSortOrder("dateOfBirth"),
                    render: (value) =>
                        moment.utc(value).toDate().toLocaleDateString(),
                },
                {
                    key: "phoneNumber",
                    dataIndex: "phoneNumber",
                    title: "Phone number",
                    width: 200,
                    sorter: true,
                    sortOrder: resolveSortOrder("phoneNumber"),
                },
                {
                    key: "stripeCustomer",
                    dataIndex: "stripeCustomer",
                    title: "Stripe customer",
                    ellipsis: true,
                    width: 180,
                    sorter: true,
                    sortOrder: resolveSortOrder("stripeCustomer"),
                    render: (value) => (
                        <Typography.Link
                            href={`https://dashboard.stripe.com/${
                                config.ENVIRONMENT === "production"
                                    ? ""
                                    : "test/"
                            }customers/${value}`}
                            target="_blank"
                        >
                            {value}
                        </Typography.Link>
                    ),
                },
                {
                    key: "remoteLockAccessPerson",
                    dataIndex: "remoteLockAccessPerson",
                    title: "RemoteLock access person",
                    ellipsis: true,
                    width: 230,
                    sorter: true,
                    sortOrder: resolveSortOrder("remoteLockAccessPerson"),
                    render: (value) => (
                        <Typography.Link
                            href={`https://connect.remotelock.com/access/user/${value}`}
                            target="_blank"
                        >
                            {value}
                        </Typography.Link>
                    ),
                },
                {
                    key: "createdAt",
                    dataIndex: ["metadata", "createdAt"],
                    title: "Created at",
                    width: 190,
                    sorter: true,
                    sortOrder: resolveSortOrder("createdAt"),
                    render: (value) =>
                        moment.utc(value).toDate().toLocaleString(),
                },
                {
                    key: "updatedAt",
                    dataIndex: ["metadata", "updatedAt"],
                    title: "Updated at",
                    width: 190,
                    sorter: true,
                    sortOrder: resolveSortOrder("updatedAt"),
                    render: (value) =>
                        moment.utc(value).toDate().toLocaleString(),
                },
            ]}
            dataSource={users}
            loading={loading}
            rowKey={(user) => user.id}
            pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ["25", "50", "100", "200"],
                style: {
                    marginRight: "15px",
                },
            }}
            onChange={(
                pagination: TablePaginationConfig,
                _,
                sorter: SorterResult<User> | SorterResult<User>[]
            ) => {
                if (!onTableChange) return;

                const singleSorter = Array.isArray(sorter)
                    ? sorter[0]
                    : sorter;

                const nextSortBy =
                    typeof singleSorter?.columnKey === "string"
                        ? (singleSorter.columnKey as UserSortBy)
                        : undefined;
                const nextSortOrder =
                    singleSorter?.order === "ascend" ||
                    singleSorter?.order === "descend"
                        ? singleSorter.order
                        : undefined;

                onTableChange(
                    pagination.current || page || 1,
                    pagination.pageSize || pageSize || 50,
                    nextSortOrder ? nextSortBy : undefined,
                    nextSortOrder
                );
            }}
            scroll={{
                y: 400,
            }}
        />
    );
}

export default UserTable;
